import React, { Suspense, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import './styles/tour.css' // Import tour styles
import { ToastProvider } from './contexts/ToastContext';
import { NewTourProvider } from './contexts/NewTourContext';
import { useAuth } from './hooks/useAuth'
import Layout from './components/EnhancedLayout'
import ErrorBoundary from './components/ErrorBoundary'
import AuthLoading from './components/AuthLoading'
import LoadingSpinner from './components/LoadingSpinner'
import EnhancedMobilePerformanceMonitor from './components/EnhancedMobilePerformanceMonitor'
import ServiceWorkerUpdateNotification from './components/ServiceWorkerUpdateNotification'
import CacheManager from './components/CacheManager'
import NewTourOverlay from './components/NewTourOverlay'
import EnvironmentErrorFallback from './components/EnvironmentErrorFallback'

import { PublicPage } from './seo/PublicContent'
import { pages } from './seo/content'
import RouteMetadata from './seo/RouteMetadata'
import PublicHome from './pages/auth/WelcomePage'

// Log environment status for debugging
console.log('🔧 App Environment Check:', {
  NODE_ENV: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
  SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? '✅ Set' : '❌ Missing',
  SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? '✅ Set' : '❌ Missing'
});

// Check for critical missing environment variables
const hasCriticalEnvVars = import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!hasCriticalEnvVars && import.meta.env.PROD) {
  console.error('🚨 Critical environment variables missing in production!');
}

// Import services conditionally to prevent initialization issues
// import { performanceMetricsService } from './services/PerformanceMetricsService'
// import { serviceWorkerManager } from './services/ServiceWorkerManager'

// Import enhanced lazy components with intelligent preloading
import {
  LazyHomePage as HomePage,
  LazyCameraPage as CameraPage,
  LazyHistoryPage as HistoryPage,
  LazyFavoritesPage as FavoritesPage,
  LazyProfilePage as ProfilePage,
  LazyAnalysisSelectionPage as AnalysisSelectionPage,
  LazySignInPage as SignInPage,
  LazySignUpPage as SignUpPage,
  LazyResetPasswordPage as ResetPasswordPage,
  LazyWelcomePage as WelcomePage,
  LazyAuthCallbackPage as AuthCallbackPage,
  LazyEmailConfirmationPage as EmailConfirmationPage,
  LazyTroubleshootingPage as TroubleshootingPage,
  LazyImageEnhancementDemo as ImageEnhancementDemo,
  LazyAccountSettingsPage as AccountSettingsPage,
  LazyAdminPanel as AdminPanel,
  LazyAnalyticsPage as AnalyticsPage,
  LazyDatabaseTestPage as DatabaseTestPage,
  LazyLiveVideoAnalysisPage as LiveVideoAnalysisPage,
  LazyLoadingDemo as LoadingDemo,
  LazyItemListingPage as ItemListingPage,
  LazyInventoryPage as InventoryPage,
} from './components/LazyComponents'

// Define new lazy components
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth()

  const { pathname } = useLocation();
  const publicPath = pathname.replace(/\/+$/, '') || '/';
  if (pages.some(page => page.path === publicPath && page.path !== '/')) return <PublicPage path={publicPath} />;
  if (publicPath === '/' && !isAuthenticated) return <PublicHome />;

  if (isLoading) {
    return <AuthLoading />
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/confirm-email" element={<EmailConfirmationPage />} />
          <Route path="/troubleshooting" element={<TroubleshootingPage />} />
          <Route path="*" element={<Navigate to="/signin" replace />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <Layout>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/camera" element={<Navigate to="/analyze" replace />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<AccountSettingsPage />} />
          <Route path="/analyze" element={<AnalysisSelectionPage />} />
          <Route path="/analyze/gemini" element={<Navigate to="/analyze" replace />} />
          <Route path="/analyze/grok" element={<Navigate to="/analyze" replace />} />
          <Route path="/analyze/serpapi" element={<Navigate to="/analyze" replace />} />
          <Route path="/analyze/searchapi" element={<Navigate to="/analyze" replace />} />
          <Route path="/analyze/decodo" element={<Navigate to="/analyze" replace />} />
          <Route path="/analyze/openlens" element={<Navigate to="/analyze" replace />} />
          <Route path="/demo/enhancements" element={<ImageEnhancementDemo />} />
          <Route path="/demo/loading" element={<LoadingDemo />} />
          <Route path="/test/database" element={<DatabaseTestPage />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/live-analysis" element={<LiveVideoAnalysisPage />} />
          <Route path="/item-listing" element={<ItemListingPage />} />
          <Route path="/inventory" element={<InventoryPage />} />
          
          {/* Redirect old paths */}
          <Route path="/saved" element={<Navigate to="/favorites" replace />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}
const App: React.FC = () => {
  // Initialize performance monitoring and service worker with error handling
  useEffect(() => {
    const initializeServices = async () => {
      try {
        // Add a small delay to ensure React is fully initialized
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Dynamically import services to prevent initialization issues
        const [
          { performanceMetricsService },
          { serviceWorkerManager }
        ] = await Promise.all([
          import('./services/PerformanceMetricsService'),
          import('./services/ServiceWorkerManager')
        ]);
        
        // Initialize performance monitoring
        if (typeof performanceMetricsService?.initializeMetricsCollection === 'function') {
          performanceMetricsService.initializeMetricsCollection();
        }
        
        // Initialize service worker
        if (typeof serviceWorkerManager?.initialize === 'function') {
          serviceWorkerManager.initialize().catch((error: any) => {
            console.error('Service Worker initialization failed:', error);
          });
        }
        
        // Send performance report after 10 seconds
        const timer = setTimeout(() => {
          if (typeof performanceMetricsService?.sendPerformanceReport === 'function') {
            performanceMetricsService.sendPerformanceReport();
          }
        }, 10000);

        return () => {
          clearTimeout(timer);
          if (typeof performanceMetricsService?.cleanup === 'function') {
            performanceMetricsService.cleanup();
          }
          if (typeof serviceWorkerManager?.cleanup === 'function') {
            serviceWorkerManager.cleanup();
          }
        };
      } catch (error) {
        console.error('Error initializing services:', error);
        return () => {}; // Return empty cleanup function
      }
    };

    let cleanup: (() => void) | undefined;
    initializeServices().then(cleanupFn => {
      cleanup = cleanupFn;
    }).catch(error => {
      console.error('Failed to initialize services:', error);
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  // Check for critical missing environment variables
  if (!hasCriticalEnvVars && import.meta.env.PROD) {
    return <EnvironmentErrorFallback />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <RouteMetadata />
        <ThemeProvider>
          <ToastProvider>
            <AuthProvider>
              <NewTourProvider>
                <EnhancedMobilePerformanceMonitor
                config={{
                  enableAdaptiveLoading: true,
                  enableBatteryOptimization: true,
                  enableConnectionAwareness: true,
                }}
                onMetricsUpdate={(metrics) => {
                  // Log metrics in development
                  if (import.meta.env.DEV) {
                    console.log('Performance metrics updated:', metrics);
                  }
                }}
                onStrategyChange={(strategy) => {
                  // Log strategy changes in development
                  if (import.meta.env.DEV) {
                    console.log('Adaptive strategy changed:', strategy);
                  }
                }}
              >
                <AppContent />
                <ServiceWorkerUpdateNotification />
                <NewTourOverlay />
                {import.meta.env.DEV && <CacheManager />}
              </EnhancedMobilePerformanceMonitor>
              </NewTourProvider>
            </AuthProvider>
          </ToastProvider>
        </ThemeProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default React.memo(App);
